import { CSSProperties } from 'react';
import { COLORS, CONTENT_COLUMN, FONTS, glass, META } from '../../globalStyles';

// Shared base for the accent-outlined "Get started" CTAs (header + mobile menu).
const ghostCta: CSSProperties = {
  ...META,
  background: 'transparent',
  border: `1px solid ${COLORS.borderAccent}`,
  color: COLORS.accent,
  borderRadius: 0,
  cursor: 'pointer',
  textDecoration: 'none',
};

export const styles: Record<string, CSSProperties> = {
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1.5rem', // minimum spacing so the logo / nav / actions never touch
    padding: 'var(--header-pad-y) var(--header-pad-x)',
    ...glass('var(--header-glass)', 10),
    borderBottom: `1px solid ${COLORS.border}`,
  },
  footer: {
    ...CONTENT_COLUMN,
    borderTop: `1px solid ${COLORS.borderStrong}`,
    padding: '2.25rem 6vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  logo: {
    color: COLORS.text,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  wordmark: {
    fontFamily: FONTS.body,
    fontSize: '1.25rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.26em',
    marginRight: '-0.26em',
    color: COLORS.text,
  },
  navLinks: {
    display: 'flex',
    gap: '2.25rem',
    listStyle: 'none',
  },
  navLink: {
    ...META,
    color: COLORS.muted,
    fontSize: '0.72rem',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.15s',
  },
  navCta: {
    ...ghostCta,
    padding: '0.55rem 1.15rem',
    fontSize: '0.68rem',
    transition: 'background 0.15s, color 0.15s',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.9rem',
  },
  // Display is controlled by the `.menu-toggle` class (hidden on desktop,
  // shown < 700px) so the media query can override it — no `display` here.
  menuToggle: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
    width: '30px',
    height: '30px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  menuBar: {
    display: 'block',
    width: '20px',
    height: '1.5px',
    background: COLORS.text,
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  menuBarTop: {
    transform: 'translateY(5.5px) rotate(45deg)',
  },
  menuBarBottom: {
    transform: 'translateY(-5.5px) rotate(-45deg)',
  },
  mobileMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    ...glass('var(--mobile-glass)', 14),
    borderBottom: `1px solid ${COLORS.border}`,
    display: 'flex',
    flexDirection: 'column',
    padding: '0.5rem 5vw 1.5rem',
  },
  mobileLink: {
    ...META,
    fontSize: '0.85rem',
    color: COLORS.muted,
    background: 'none',
    border: 'none',
    borderBottom: `1px solid ${COLORS.border}`,
    cursor: 'pointer',
    textAlign: 'left',
    padding: '1.1rem 0',
    width: '100%',
  },
  mobileCta: {
    ...ghostCta,
    marginTop: '1.35rem',
    padding: '0.9rem',
    fontSize: '0.72rem',
    textAlign: 'center',
    width: '100%',
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
  },
  footerCopy: {
    ...META,
    fontSize: '0.68rem',
    color: COLORS.label,
  },
  footerLinks: {
    display: 'flex',
    gap: '1.75rem',
  },
  // reCAPTCHA disclosure (required by Google's terms while the badge is hidden).
  // flexBasis 100% drops it onto its own full-width row beneath the brand + nav.
  footerLegal: {
    flexBasis: '100%',
    fontFamily: FONTS.body,
    fontSize: '0.62rem',
    lineHeight: 1.6,
    color: COLORS.faint,
    textAlign: 'center',
    marginTop: '0.35rem',
  },
  footerLegalLink: {
    color: COLORS.muted,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  },
  footerLink: {
    ...META,
    fontSize: '0.68rem',
    color: COLORS.label,
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.15s',
  },
};
